import {
  APIModelAttributes,
  APIReferenceType,
  APIScopeData,
  ArdoqId,
} from '@ardoq/api-types';
import { DEFAULT_REFERENCE_TYPES } from './model';
import { Locale, localeCompare } from '@ardoq/locale';

/**
 * Read reference types from workspace model, ensuring that the implicit
 * reference type is always present.
 */
const getReferenceTypes = (model: APIModelAttributes) => {
  const { referenceTypes } = model;

  // defensive programming because it is based older code
  if (!referenceTypes || Object.keys(referenceTypes).length === 0) {
    return DEFAULT_REFERENCE_TYPES;
  }

  if (referenceTypes[2]) {
    return referenceTypes;
  }

  return { ...referenceTypes, [2]: DEFAULT_REFERENCE_TYPES[2] };
};

/**
 * Transforms the referenceTypes to an index with a consistent key-type
 * - from { [string | number]: APIReferenceType }
 * - to Map<number, APIReferenceType>
 */
const _toReferenceTypesIndex = (
  referenceTypeRecord: Record<string | number, APIReferenceType>
): ReferenceTypesIndex => {
  const typeIds = Object.keys(referenceTypeRecord).map(Number);
  const referenceTypesIndex = new Map(
    typeIds.map(typeId => [typeId, referenceTypeRecord[typeId]])
  );

  return referenceTypesIndex;
};

type ReferenceTypeLookup = Map<ArdoqId, Map<number, APIReferenceType>>;
const makeReferenceTypeLookup = (
  models: APIModelAttributes[]
): ReferenceTypeLookup =>
  new Map(models.map(model => [model._id, getReferenceTypesMap(model)]));

const getReferenceType = (
  lookup: ReferenceTypeLookup,
  modelId: ArdoqId,
  referenceTypeId: number
) => lookup.get(modelId)?.get(referenceTypeId);

/**
 * Get referenceTypes as a Map<number, APIReferenceType> as opposed to an index
 * on keys of type string | number.
 */
const getReferenceTypesMap = (
  model: APIModelAttributes
): ReferenceTypesIndex => {
  const referenceTypes = getReferenceTypes(model);
  return _toReferenceTypesIndex(referenceTypes);
};

type ReferenceTypesIndex = Map<number, APIReferenceType>;

const createReferenceTypeNameComparator = (locale: Locale) => {
  return (a: APIReferenceType, b: APIReferenceType) => {
    return localeCompare(a.name, b.name, locale);
  };
};

const getSortedReferenceTypes = (
  referenceTypes: ReferenceTypesIndex,
  locale: Locale
) => {
  return Array.from(referenceTypes.values()).sort(
    createReferenceTypeNameComparator(locale)
  );
};

type GetDefaultReferenceTypeForModelContext = {
  modelId: string;
  locale: Locale;
};

const getDefaultReferenceTypeForModel = (
  scopeData: APIScopeData,
  { modelId, locale }: GetDefaultReferenceTypeForModelContext
): APIReferenceType | undefined => {
  const model = scopeData.models.find(({ _id }) => _id === modelId);
  if (!model) {
    return undefined;
  }

  const typeIdsIndex = modelOperations.getReferenceTypesMap(model);
  const sortedReferenceTypes = getSortedReferenceTypes(typeIdsIndex, locale);

  return sortedReferenceTypes[0];
};

export const modelOperations = {
  getReferenceTypes,
  getReferenceTypesMap,
  getDefaultReferenceTypeForModel,
  makeReferenceTypeLookup,
  getReferenceType,
};
